import React, { useState, useContext, useEffect, useCallback } from 'react'
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Copy, CreditCard, ExternalLink, Search, Shield, Loader2, Download } from 'lucide-react'
import { Credential, SearchHistory } from 'api-client';
import { useNavigate } from 'react-router-dom';
import { LeaksApi } from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import { AuthContext } from '@/contexts/AuthContext';
import { useTeams } from '@/contexts/TeamsContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/Badge';
import { TablePagination } from '@/components/ui/TablePagination';
import { toast } from 'sonner';

const formatTimestamp = (timestamp: Date | string | undefined): string => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (diffDay > 0) {
    return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  } else if (diffHour > 0) {
    return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  } else if (diffMin > 0) {
    return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
};

const CredentialLeakSearch = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [allLeaks, setAllLeaks] = useState<Credential[]>([]);
    const [paginatedLeaks, setPaginatedLeaks] = useState<Credential[]>([]);
    const ITEMS_PER_PAGE = 10; // Number of items to show per page
    const [searchHistory, setSearchHistory] = useState<SearchHistory[]>([]);
    const [paginationInfo, setPaginationInfo] = useState({
        count: 0,
        next: "",
        previous: "",
    });
    const [searchHistoryPaginationInfo, setSearchHistoryPaginationInfo] = useState({
        count: 0,
        next: "",
        previous: "",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchHistoryCurrentPage, setSearchHistoryCurrentPage] = useState(1);
    const [visiblePasswords, setVisiblePasswords] = useState<Record<number, boolean>>({});
    const navigate = useNavigate();
    const { currentTeam, refetchCurrentTeam } = useTeams();
    const { token } = useContext(AuthContext);

    // Credit information
    const [creditInfo, setCreditInfo] = useState<{ available: number; total: number }>({ available: 0, total: 100 });

    const fetchCreditInfo = useCallback(async () => {
        if (!currentTeam?.slug || !token) return;
        
        if (currentTeam && typeof currentTeam.stealerLeaksCredits === 'number') {
            setCreditInfo({
                available: currentTeam.stealerLeaksCredits,
                total: 100
            });
            return;
        }
        
        try {
            setCreditInfo({
                available: 0,
                total: 100
            });
        } catch (err: any) {
            console.error('Error handling credit information:', err);
        }
    }, [currentTeam, token]);

    useEffect(() => {
        fetchCreditInfo();
    }, [fetchCreditInfo]);
    
    // Update paginated results whenever allLeaks or currentPage changes
    useEffect(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setPaginatedLeaks(allLeaks.slice(startIndex, endIndex));
    }, [allLeaks, currentPage, ITEMS_PER_PAGE]);
    
    const LeaksSearch = async (queryOverride?: string) => {
        try {
            if (!token || !currentTeam) return;
            const apiClient = new LeaksApi(getApiConfiguration(token));
            setIsLoading(true);
            const response = await apiClient.credentialSearch({
                query: queryOverride || searchQuery,
                teamSlug: currentTeam?.slug || ''
            });
            
            // Store all results
            setAllLeaks(response);
            
            // Set pagination info based on total results
            setPaginationInfo({
                count: response.length,
                next: "",
                previous: "",
            });
            
            // Reset to first page when performing a new search
            setCurrentPage(1);
            
            await refetchCurrentTeam();
            fetchCreditInfo();
        } catch (error) {
            console.error('Error searching for leaks:', error);
            toast.error(error instanceof Error ? error.message : 'An error occurred while searching for leaks.');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSearchHistory = async () => {
        try {
            if (!token || !currentTeam) return;
            const apiClient = new LeaksApi(getApiConfiguration(token));
            let response = await apiClient.leaksSearchHistoryList({
                teamSlug: currentTeam?.slug || '',
                page: searchHistoryCurrentPage,
                pageSize: 7,
            });
            // response = {
            //     "count": 123,
            //     "next": "http://api.example.org/accounts/?page=4",
            //     "previous": "http://api.example.org/accounts/?page=2",
            //     "results": [
            //         {
            //             "id": 1,
            //             "searchQuery": "admin@company.com",
            //             "createdAt": new Date("2025-04-13T18:56:13.579Z"),
            //             "updatedAt": new Date("2025-04-13T18:56:13.579Z")
            //         },
            //         {
            //             "id": 2,
            //             "searchQuery": "api_key_",
            //             "createdAt": new Date("2025-04-12T14:22:45.123Z"),
            //             "updatedAt": new Date("2025-04-12T14:22:45.123Z")
            //         },
            //         {
            //             "id": 3,
            //             "searchQuery": "company.com",
            //             "createdAt": new Date("2025-04-10T09:18:30.456Z"),
            //             "updatedAt": new Date("2025-04-10T09:18:30.456Z")
            //         },
            //         {
            //             "id": 4,
            //             "searchQuery": "password123",
            //             "createdAt": new Date("2025-04-08T16:42:11.789Z"),
            //             "updatedAt": new Date("2025-04-08T16:42:11.789Z")
            //         },
            //         {
            //             "id": 5,
            //             "searchQuery": "dev@company.com",
            //             "createdAt": new Date("2025-04-05T11:33:27.321Z"),
            //             "updatedAt": new Date("2025-04-05T11:33:27.321Z")
            //         }
            //     ]
            // }
            setSearchHistory(response.results);
            setSearchHistoryPaginationInfo({
                count: response.count,
                next: response.next || "",
                previous: response.previous || ""
            });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'An error occurred while fetching search history.');
        }
    };

    useEffect(() => {
        if (token) {
            fetchSearchHistory();
        }
    }, [token, currentTeam, searchHistoryCurrentPage]);

    const handleSearchHistoryPageChange = (page: number) => {
        setSearchHistoryCurrentPage(page);
        fetchSearchHistory();
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        // No need to call API again, just update the paginated results
    };
    
    return (
        <div className="flex w-full flex-col">
            <div className="flex justify-between items-center">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-white flex items-center">
                        <Shield className="mr-2 h-6 w-6 text-blue-400" />
                        Third Party Credential Leak Dark Web Search
                    </h1>
                    <p className="text-gray-400 mt-1">Search for leaked credentials on the Dark Web to protect your organization.</p>
                </div>
                <Button
              variant="outline"
              onClick={() => {
                  if (allLeaks && allLeaks.length > 0) {
                    const headers = Object.keys(allLeaks[0]);
                    const csvRows = allLeaks.map(leak => 
                      headers.map(header => 
                        typeof (leak as any)[header] === 'object' ? JSON.stringify((leak as any)[header]) : (leak as any)[header]
                      ).join(',')
                    );
                    const csvContent = [headers.join(','), ...csvRows].join('\n');
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'stealer_leaks.csv';
                    link.click();
                    URL.revokeObjectURL(url);
                  }
              }}
              disabled={allLeaks?.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            </div>
            
            {/* Loading Dialog */}
            {isLoading && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-[#1A1F2E] rounded-xl border border-blue-800/50 p-6 max-w-md w-full shadow-lg shadow-blue-500/10 animate-fadeIn">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                                <div className="h-16 w-16 rounded-full bg-blue-500/20 flex items-center justify-center">
                                    <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
                                </div>
                                <div className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 rounded-full animate-pulse"></div>
                            </div>
                            <h3 className="text-xl font-semibold text-white">Searching Dark Web</h3>
                            <p className="text-gray-400 text-center">We're scanning the Dark Web for your credentials. This may take a moment as we search through secure databases.</p>
                            <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden mt-2">
                                <div className="bg-blue-500 h-full animate-progressBar"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="flex w-full">
            <div className="flex-1 max-w-[80%] 2xl:max-w-[75%] xl:max-w-[73%] lg:max-w-[73%] md:max-w-[calc(100vw-300px)]">
                <div className="space-y-6">
                    <div className="flex-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        LeaksSearch();
                                    }
                                }}
                                className="w-full pl-10 pr-4 py-2 bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            />
                            <Button
                                onClick={() => LeaksSearch()}
                                className="absolute right-2 bg-gray-300 top-1/2 transform -translate-y-1/2 px-3 py-1 text-sm"
                                variant="ghost"
                            >
                                Search
                            </Button>
                        </div>
                    </div>
                    {/* Leaks Table */}
                    <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-800">
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Identifier</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Password</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">URL</th>
                                    {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Service</th> */}
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Type</th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Created At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                                <span>Loading credentials...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-red-400">
                                            {error}
                                        </td>
                                    </tr>
                                ) : paginatedLeaks.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-gray-400">
                                            No stealer leaks found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedLeaks.map((leak: Credential) => (
                                        <tr key={leak.id} className="hover:bg-gray-900/30">
                                            <td
                                                className="px-6 py-4 text-sm text-gray-300 cursor-pointer"
                                                onClick={() => {
                                                    navigate(`/data-protection/stealer-leaks/${leak.id}`);
                                                }}
                                            >
                                                {leak.identifier}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300">
                                                <div className="flex items-center space-x-2">
                                                    <span
                                                        className="font-mono cursor-pointer"
                                                        onClick={() => {
                                                            // Toggle password visibility for this specific leak
                                                            setVisiblePasswords(prev => ({
                                                                ...prev,
                                                                [leak.id]: !prev[leak.id]
                                                            }));
                                                        }}
                                                    >
                                                        {visiblePasswords[leak.id] ? leak.password : '•••••••••'}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(leak.password || '');
                                                        }}
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300">
                                                <div className="flex items-center space-x-2">
                                                    <span className="truncate max-w-[200px]">{leak.url}</span>
                                                    {leak.url && (
                                                        <a
                                                            href={leak.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-500 hover:text-blue-400"
                                                        >
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                            {/* <td className="px-6 py-4 text-sm text-gray-300">{leak.domainName}</td>
                                            <td className="px-6 py-4 text-sm text-gray-300">{leak.serviceName}</td> */}
                                            <td className="px-6 py-4">
                                                <Badge
                                                    variant={leak.isMobile ? "info" : "warning"}
                                                >
                                                    {leak.isMobile ? 'Mobile' : 'Desktop'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-300">
                                                {leak.createdAt ? new Date(leak.createdAt).toLocaleDateString() : ''}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {!isLoading && paginatedLeaks.length > 0 && (
                        <div className="mt-4">
                            <TablePagination
                                currentPage={currentPage}
                                totalItems={paginationInfo.count}
                                itemsPerPage={10}
                                onPageChange={handlePageChange}
                                paginationInfo={{
                                    count: paginationInfo.count,
                                    next: paginationInfo.next,
                                    previous: paginationInfo.previous,
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="ml-6 w-[25%] xl:w-[25%] lg:w-[27%] flex-shrink-0">
                <div className="bg-[#1A1F2E] rounded-lg border border-gray-800">
                    <div className="p-3 border-b border-gray-800">
                        <h2 className="text-md font-semibold text-white flex items-center">
                            Search History
                        </h2>
                    </div>
                    <div className="space-y-2 p-3">
                        {searchHistory.map((search, index) => (
                            <div
                                key={search.id || index}
                                className="flex items-center justify-between p-2 rounded-lg bg-gray-900/30 hover:bg-gray-900/50 cursor-pointer"
                                onClick={() => {
                                    setSearchQuery(search.searchQuery);
                                    LeaksSearch(search.searchQuery);
                                }}
                            >
                                <span className="text-gray-300">{search.searchQuery}</span>
                                <span className="text-gray-400 text-sm">{formatTimestamp(search.createdAt)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4">
                        <TablePagination
                            currentPage={searchHistoryCurrentPage}
                            totalItems={searchHistoryPaginationInfo.count}
                            itemsPerPage={7}
                            onPageChange={handleSearchHistoryPageChange}
                            paginationInfo={{
                                count: searchHistoryPaginationInfo.count,
                                next: searchHistoryPaginationInfo.next,
                                previous: searchHistoryPaginationInfo.previous,
                            }}
                            itemName="searches"
                            showText={false}
                            nextIcon={<ChevronRight />}
                            previousIcon={<ChevronLeft />}
                        />
                    </div>
                </div>
                <div className="mt-8 bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-xl p-4 backdrop-blur-sm border border-blue-800/50 transition-all hover:shadow-md hover:shadow-blue-500/10">
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-300">Stealer Leaks Credits</span>
                            <div className="p-1.5 rounded-lg bg-blue-500/10">
                                <CreditCard className="h-4 w-4 text-blue-400" />
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-white">{creditInfo.available}</div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}

const styleSheet = document.styleSheets[0];
if (styleSheet) {
    if (!document.querySelector('style[data-animation="fadeIn"]')) {
        const fadeInStyle = document.createElement('style');
        fadeInStyle.setAttribute('data-animation', 'fadeIn');
        fadeInStyle.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; transform: scale(0.95); }
                to { opacity: 1; transform: scale(1); }
            }
            .animate-fadeIn {
                animation: fadeIn 0.3s ease-out forwards;
            }
            @keyframes progressBar {
                0% { width: 0%; }
                20% { width: 20%; }
                50% { width: 50%; }
                80% { width: 80%; }
                100% { width: 95%; }
            }
            .animate-progressBar {
                animation: progressBar 2s ease-in-out infinite alternate;
            }
        `;
        document.head.appendChild(fadeInStyle);
    }
}

export default CredentialLeakSearch